import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, forkJoin, map, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { CardsService } from './cards.service';
import type { DigitalCard } from './cards.service';

export interface Bookmark {
  cardId: string;
  userId: string;
  createdAt?: any;
  updatedAt?: any;
  cardData?: any;
}

@Injectable({
  providedIn: 'root',
})
export class BookmarkUiService {
  private apiBase = environment.apiUrl;

  constructor(private http: HttpClient, private cardsService: CardsService) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('idToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
  }

  addBookmark(userId: string, cardId: string): Observable<any> {
    return this.http.post(
      `${this.apiBase}/bookmark/${userId}/${cardId}`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getBookmarks(userId: string): Observable<Bookmark[]> {
    return this.http.get<Bookmark[]>(
      `${this.apiBase}/bookmarks/${userId}`,
      { headers: this.getHeaders() }
    );
  }

  removeBookmark(userId: string, cardId: string): Observable<any> {
    return this.http.delete(
      `${this.apiBase}/bookmark/${userId}/${cardId}`,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Helper: Get all bookmarks for a user, and fetch full card data for each.
   * Returns Observable of array: { bookmark, card }
   */
  getBookmarksWithCardData(userId: string): Observable<{ bookmark: Bookmark; card: DigitalCard | null }[]> {
    return this.getBookmarks(userId).pipe(
      switchMap((bookmarks: Bookmark[]) => {
        if (!bookmarks?.length) return from([[]]);
        // For each bookmark, fetch the card data
        const cardRequests = bookmarks.map(b =>
          from(this.cardsService.getCardById(b.cardId).catch(() => null))
        );
        return forkJoin(cardRequests).pipe(
          map((cards: (DigitalCard | null)[]) =>
            bookmarks.map((bookmark, i) => ({
              bookmark,
              card: cards[i]
            }))
          )
        );
      })
    );
  }
}